import React, { useEffect, useMemo, useState } from 'react';
import { Form as FinalForm, FormSpy } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { numberAtLeast, required } from '../../../util/validators';
import { PURCHASE_PROCESS_NAME } from '../../../transactions/transaction';

import {
  Form,
  FieldSelect,
  FieldTextInput,
  InlineTextButton,
  PrimaryButton,
  SecondaryButton,
  H3,
  H6,
  ExternalLink,
} from '../../../components';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';
import { OrderBreakdownPlaceholder } from '../../OrderBreakdown/OrderBreakdown';

import FetchLineItemsError from '../FetchLineItemsError/FetchLineItemsError.js';

import { ReactComponent as ProtectionIcon } from '../../../assets/ProtectionIcon.svg';
import getCountryCodes from '../../../translations/countryCodes';

import css from './ProductOrderForm.module.css';

// Browsers can't render huge number of select options.
// (stock is shown inside select element)
// Note: input element could allow ordering bigger quantities
const MAX_QUANTITY_FOR_DROPDOWN = 100;

// A line-items fetch fires (see handleFetchLineItems) when there is a quantity
// and the delivery selection is complete. Shared with the breakdown placeholder
// so "reserve space" and "fetch will happen" can never drift apart.
const willFetchLineItems = ({ quantity, deliveryMethod, shippingCountry, displayDeliveryMethod }) =>
  !!Number.parseInt(quantity, 10) &&
  (!displayDeliveryMethod || !!deliveryMethod) &&
  (deliveryMethod !== 'shipping' || !!shippingCountry);

const handleFetchLineItems = ({
  quantity,
  deliveryMethod,
  shippingCountry,
  displayDeliveryMethod,
  listingId,
  isOwnListing,
  fetchLineItemsInProgress,
  onFetchTransactionLineItems,
}) => {
  const stockReservationQuantity = Number.parseInt(quantity, 10);
  const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};
  const shippingCountryMaybe =
    deliveryMethod === 'shipping' && shippingCountry ? { shippingCountry } : {};
  const isBrowser = typeof window !== 'undefined';
  if (
    isBrowser &&
    willFetchLineItems({ quantity, deliveryMethod, shippingCountry, displayDeliveryMethod }) &&
    !fetchLineItemsInProgress
  ) {
    onFetchTransactionLineItems({
      orderData: { stockReservationQuantity, ...deliveryMethodMaybe, ...shippingCountryMaybe },
      listingId,
      isOwnListing,
    });
  }
};

const DeliveryMethodMaybe = props => {
  const {
    displayDeliveryMethod,
    hasMultipleDeliveryMethods,
    deliveryMethod,
    hasStock,
    formId,
    intl,
  } = props;
  const showDeliveryMethodSelector = displayDeliveryMethod && hasMultipleDeliveryMethods;
  const showSingleDeliveryMethod = displayDeliveryMethod && deliveryMethod;
  return !hasStock ? null : showDeliveryMethodSelector ? (
    <FieldSelect
      id={`${formId}.deliveryMethod`}
      className={css.deliveryField}
      name="deliveryMethod"
      label={intl.formatMessage({ id: 'ProductOrderForm.deliveryMethodLabel' })}
      validate={required(intl.formatMessage({ id: 'ProductOrderForm.deliveryMethodRequired' }))}
    >
      <option disabled value="">
        {intl.formatMessage({ id: 'ProductOrderForm.selectDeliveryMethodOption' })}
      </option>
      <option value={'pickup'}>
        {intl.formatMessage({ id: 'ProductOrderForm.pickupOption' })}
      </option>
      <option value={'shipping'}>
        {intl.formatMessage({ id: 'ProductOrderForm.shippingOption' })}
      </option>
    </FieldSelect>
  ) : showSingleDeliveryMethod ? (
    <div className={css.deliveryField}>
      <H3 rootClassName={css.singleDeliveryMethodLabel}>
        {intl.formatMessage({ id: 'ProductOrderForm.deliveryMethodLabel' })}
      </H3>
      <p className={css.singleDeliveryMethodSelected}>
        {deliveryMethod === 'shipping'
          ? intl.formatMessage({ id: 'ProductOrderForm.shippingOption' })
          : intl.formatMessage({ id: 'ProductOrderForm.pickupOption' })}
      </p>
      <FieldTextInput
        id={`${formId}.deliveryMethod`}
        className={css.deliveryField}
        name="deliveryMethod"
        type="hidden"
      />
    </div>
  ) : (
    <FieldTextInput
      id={`${formId}.deliveryMethod`}
      className={css.deliveryField}
      name="deliveryMethod"
      type="hidden"
    />
  );
};

const renderForm = (formRenderProps, extraProps = {}) => {
  const [mounted, setMounted] = useState(false);
  const {
    // FormRenderProps from final-form
    handleSubmit,
    form: formApi,

    // Custom props passed to the form component
    intl,
    formId,
    currentStock,
    allowOrdersOfMultipleItems,
    hasMultipleDeliveryMethods,
    displayDeliveryMethod,
    listingId,
    isOwnListing,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    price,
    payoutDetailsWarning,
    marketplaceName,
    values,
    pickupEnabled,
    shippingEnabled,
    buyerProtectionLink,
  } = formRenderProps;
  const {
    handlePurchaseSubmit,
    onlyMakeOffer = false,
    showMakeOfferButton = false,
    onOpenMakeOffer,
    makeOfferInProgress = false,
    makeOfferError,
  } = extraProps;

  const locale = intl?.locale || 'en';
  const { shippingCountries } = formRenderProps;
  const countryOptions = useMemo(() => {
    const allowedSet =
      Array.isArray(shippingCountries) && shippingCountries.length > 0
        ? new Set(shippingCountries)
        : null;
    return getCountryCodes(locale)
      .map(({ code, name }) => ({ code, name }))
      .filter(c => !allowedSet || allowedSet.has(c.code))
      .sort((a, b) => a.name.localeCompare(b.name, locale, { sensitivity: 'base' }));
  }, [locale, shippingCountries]);
  const offerAmountPositiveMessage = intl.formatMessage({
    id: 'ProductOrderForm.offerAmountPositive',
  });
  const offerAmountRequiredMessage = intl.formatMessage({
    id: 'ProductOrderForm.offerAmountRequired',
  });
  const offerAmountValidator = value => {
    if (!value) {
      return offerAmountRequiredMessage;
    }
    if (typeof value.amount !== 'number' || value.amount <= 0) {
      return offerAmountPositiveMessage;
    }
    return undefined;
  };
  const purchaseSubmitHandler =
    typeof handlePurchaseSubmit === 'function' ? handlePurchaseSubmit : handleSubmit;

  // Note: don't add custom logic before useEffect
  useEffect(() => {
    setMounted(true);

    // Side-effect: fetch line-items after mounting if possible
    const { quantity, deliveryMethod, shippingCountry } = values;
    if (quantity && !formRenderProps.hasMultipleDeliveryMethods) {
      handleFetchLineItems({
        quantity,
        deliveryMethod,
        shippingCountry,
        displayDeliveryMethod,
        listingId,
        isOwnListing,
        fetchLineItemsInProgress,
        onFetchTransactionLineItems,
      });
    }
  }, []);

  // If form values change, update line-items for the order breakdown
  const handleOnChange = formValues => {
    const { quantity, deliveryMethod, shippingCountry } = formValues.values;
    if (mounted) {
      if (deliveryMethod !== 'shipping') {
        const shippingCountryState =
          typeof formApi.getFieldState === 'function'
            ? formApi.getFieldState('shippingCountry')
            : null;
        const shouldResetShippingCountry =
          shippingCountry ||
          (shippingCountryState &&
            (shippingCountryState.touched || shippingCountryState.error || shippingCountryState.dirty));

        if (shouldResetShippingCountry) {
          if (shippingCountry) {
            formApi.change('shippingCountry', null);
          }
          if (typeof formApi.resetFieldState === 'function') {
            formApi.resetFieldState('shippingCountry');
          }
        }
      }
      handleFetchLineItems({
        quantity,
        deliveryMethod,
        shippingCountry,
        listingId,
        isOwnListing,
        fetchLineItemsInProgress,
        onFetchTransactionLineItems,
      });
    }
  };

  // In case quantity and deliveryMethod are missing focus on that select-input.
  // Otherwise continue with the default handleSubmit function.
  const handleFormSubmit = e => {
    const { quantity, deliveryMethod, shippingCountry } = values || {};
    if (!quantity || quantity < 1) {
      e.preventDefault();
      // Blur event will show validator message
      formApi.blur('quantity');
      formApi.focus('quantity');
    } else if (displayDeliveryMethod && !deliveryMethod) {
      e.preventDefault();
      // Blur event will show validator message
      formApi.blur('deliveryMethod');
      formApi.focus('deliveryMethod');
    } else if (deliveryMethod === 'shipping' && !shippingCountry) {
      e.preventDefault();
      formApi.blur('shippingCountry');
      formApi.focus('shippingCountry');
    } else {
      handleSubmit(e);
    }
  };

  const breakdownData = {};
  const showBreakdown =
    breakdownData && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;

  const showContactUser = typeof onContactUser === 'function';

  const onClickContactUser = e => {
    e.preventDefault();
    onContactUser();
  };

  const contactSellerLink = (
    <InlineTextButton onClick={onClickContactUser}>
      <FormattedMessage id="ProductOrderForm.finePrintNoStockLinkText" />
    </InlineTextButton>
  );
  const quantityRequiredMsg = intl.formatMessage({ id: 'ProductOrderForm.quantityRequired' });

  // Listing is out of stock if currentStock is zero. OrderPanel renders this
  // form only once stock has loaded, so currentStock is always a number here.
  const hasNoStockLeft = currentStock === 0;
  const hasStock = currentStock > 0;
  const hasOneItemLeft = currentStock === 1;
  const selectableStock = Math.min(currentStock, MAX_QUANTITY_FOR_DROPDOWN);
  const quantities = hasStock ? [...Array(selectableStock).keys()].map(i => i + 1) : [];

  const submitInProgress = fetchLineItemsInProgress;
  const submitDisabled = !hasStock;
  const deliveryMethod = values?.deliveryMethod;
  const fallbackDeliveryMethod =
    !hasMultipleDeliveryMethods && displayDeliveryMethod
      ? shippingEnabled && !pickupEnabled
        ? 'shipping'
        : pickupEnabled && !shippingEnabled
        ? 'pickup'
        : deliveryMethod
      : null;
  const deliveryNoteType = deliveryMethod || fallbackDeliveryMethod;
  // A breakdown renders once line items arrive; reserve its space as soon as a
  // fetch is possible (pickup right away, shipping after a country is chosen)
  // so the content below does not jump.
  const expectBreakdown =
    hasStock &&
    !fetchLineItemsError &&
    willFetchLineItems({
      quantity: values?.quantity,
      deliveryMethod: deliveryNoteType,
      shippingCountry: values?.shippingCountry,
      displayDeliveryMethod,
    });
  const deliveryNoteMessageId =
    deliveryNoteType === 'shipping'
      ? 'ProductOrderForm.shippingDeliveryNote'
      : deliveryNoteType === 'pickup'
      ? 'ProductOrderForm.pickupDeliveryNote'
      : null;
  const deliveryNoteLink = '/how-to-buy';

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormSpy subscription={{ values: true }} onChange={handleOnChange} />
      {hasNoStockLeft ? null : hasOneItemLeft || !allowOrdersOfMultipleItems ? (
        <FieldTextInput
          id={`${formId}.quantity`}
          className={css.quantityField}
          name="quantity"
          type="hidden"
          validate={numberAtLeast(quantityRequiredMsg, 1)}
        />
      ) : (
        <FieldSelect
          id={`${formId}.quantity`}
          className={css.quantityField}
          name="quantity"
          disabled={!hasStock}
          label={intl.formatMessage({ id: 'ProductOrderForm.quantityLabel' })}
          validate={numberAtLeast(quantityRequiredMsg, 1)}
        >
          <option disabled value="">
            {intl.formatMessage({ id: 'ProductOrderForm.selectQuantityOption' })}
          </option>
          {quantities.map(quantity => (
            <option key={quantity} value={quantity}>
              {intl.formatMessage({ id: 'ProductOrderForm.quantityOption' }, { quantity })}
            </option>
          ))}
        </FieldSelect>
      )}

      <DeliveryMethodMaybe
        displayDeliveryMethod={displayDeliveryMethod}
        hasMultipleDeliveryMethods={hasMultipleDeliveryMethods}
        deliveryMethod={values?.deliveryMethod}
        hasStock={hasStock}
        formId={formId}
        intl={intl}
      />

      {displayDeliveryMethod && deliveryNoteMessageId ? (
        <p className={css.deliveryInfo}>
          <FormattedMessage
            id={deliveryNoteMessageId}
            values={{
              a: chunks => (
                <ExternalLink href={deliveryNoteLink} className={css.deliveryInfoLink}>
                  {chunks}
                </ExternalLink>
              ),
            }}
          />
        </p>
      ) : null}

      {hasStock && shippingEnabled && values?.deliveryMethod === 'shipping' ? (
        <>
          <label className={css.shippingCountryLabel} htmlFor={`${formId}.shippingCountry`}>
            {intl.formatMessage({ id: 'ProductOrderForm.shippingCountryLabel' })}
          </label>
          <FieldSelect
            id={`${formId}.shippingCountry`}
            className={css.shippingCountryField}
            name="shippingCountry"
            validate={required(intl.formatMessage({ id: 'ProductOrderForm.shippingCountryRequired' }))}
          >
            <option disabled value="">
              {Array.isArray(shippingCountries) && shippingCountries.length > 0
                ? `${intl.formatMessage({ id: 'ProductOrderForm.selectShippingCountryOption' })} - ${intl.formatMessage({ id: 'ProductOrderForm.shippingCountriesLimitedShort' })}`
                : intl.formatMessage({ id: 'ProductOrderForm.selectShippingCountryOption' })}
            </option>
            {countryOptions.map(option => (
              <option key={option.code} value={option.code}>
                {option.name}
              </option>
            ))}
          </FieldSelect>
          <p className={css.shippingCountryNote}>
            <FormattedMessage id="ProductOrderForm.shippingCountryNote" />
            {Array.isArray(shippingCountries) && shippingCountries.length > 0 ? (
              <>
                {' '}
                <FormattedMessage id="ProductOrderForm.shippingCountriesLimited" />
              </>
            ) : null}
          </p>
        </>
      ) : (
        <FieldTextInput
          id={`${formId}.shippingCountry`}
          className={css.deliveryField}
          name="shippingCountry"
          type="hidden"
          hideErrorMessage
        />
      )}

      {showBreakdown || expectBreakdown ? (
        <div className={css.breakdownWrapper}>
          <H6 as="h3" className={css.bookingBreakdownTitle}>
            <FormattedMessage id="ProductOrderForm.breakdownTitle" />
          </H6>
          <hr className={css.totalDivider} />
          {showBreakdown ? (
            <EstimatedCustomerBreakdownMaybe
              breakdownData={breakdownData}
              lineItems={lineItems}
              currency={price.currency}
              marketplaceName={marketplaceName}
              processName={PURCHASE_PROCESS_NAME}
            />
          ) : (
            <OrderBreakdownPlaceholder />
          )}
        </div>
      ) : null}

      <FetchLineItemsError error={fetchLineItemsError} />

      {!onlyMakeOffer ? (
        <div className={css.submitButton}>
          <PrimaryButton
            type="submit"
            inProgress={submitInProgress}
            disabled={submitDisabled}
            onClick={purchaseSubmitHandler}
          >
            {hasStock ? (
              <FormattedMessage id="ProductOrderForm.ctaButton" />
            ) : (
              <FormattedMessage id="ProductOrderForm.ctaButtonNoStock" />
            )}
          </PrimaryButton>
        </div>
      ) : null}

      {showMakeOfferButton && !onlyMakeOffer ? (
        <div className={css.makeOfferButtonRow}>
          <SecondaryButton
            type="button"
            onClick={onOpenMakeOffer}
            inProgress={makeOfferInProgress}
            className={css.makeOfferButton}
          >
            <FormattedMessage id="ProductOrderForm.makeOfferButton" />
          </SecondaryButton>
          {makeOfferError ? (
            <p className={css.makeOfferInlineError}>
              <FormattedMessage id="ProductOrderForm.makeOfferError" />
            </p>
          ) : null}
        </div>
      ) : null}

      <div className={css.protectionCard}>
        <ProtectionIcon className={css.protectionIcon} aria-hidden="true" focusable="false" />
        <div className={css.protectionContent}>
          <p className={css.protectionText}>
            <FormattedMessage id="ProductOrderForm.buyerProtectionDescription" />
          </p>
          {buyerProtectionLink ? (
            <ExternalLink href={buyerProtectionLink} className={css.protectionLink}>
              <FormattedMessage id="ProductOrderForm.buyerProtectionLearnMore" />
            </ExternalLink>
          ) : (
            <span className={css.protectionLink}>
              <FormattedMessage id="ProductOrderForm.buyerProtectionLearnMore" />
            </span>
          )}
        </div>
      </div>
    </Form>
  );
};

/**
 * A form for ordering a product.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.marketplaceName - The name of the marketplace
 * @param {string} props.formId - The ID of the form
 * @param {Function} props.onSubmit - The function to handle the form submission
 * @param {propTypes.uuid} props.listingId - The ID of the listing
 * @param {propTypes.money} props.price - The price of the listing
 * @param {number} props.currentStock - The current stock of the listing
 * @param {boolean} props.isOwnListing - Whether the listing is owned by the current user
 * @param {boolean} props.pickupEnabled - Whether pickup is enabled
 * @param {boolean} props.shippingEnabled - Whether shipping is enabled
 * @param {boolean} props.displayDeliveryMethod - Whether the delivery method is displayed
 * @param {Object} props.lineItems - The line items
 * @param {Function} props.onFetchTransactionLineItems - The function to fetch the transaction line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the line items are being fetched
 * @param {propTypes.error} props.fetchLineItemsError - The error for fetching the line items
 * @param {Function} props.onContactUser - The function to contact the user
  * @param {boolean} props.showMakeOfferButton - Whether to render the make offer action next to the purchase CTA
  * @param {Function} props.onOpenMakeOffer - Handler for opening the make offer flow
  * @param {boolean} props.makeOfferInProgress - Whether the make offer action is in progress
  * @param {propTypes.error} props.makeOfferError - Error from the make offer action
 * @returns {JSX.Element}
 */
const ProductOrderForm = props => {
  const intl = useIntl();
  const {
    price,
    currentStock,
    pickupEnabled,
    shippingEnabled,
    shippingCountries,
    displayDeliveryMethod,
    allowOrdersOfMultipleItems,
    buyerProtectionLink,
    onSubmit,
    onlyMakeOffer = false,
    showMakeOfferButton = false,
    onOpenMakeOffer,
    makeOfferInProgress = false,
    makeOfferError,
    ...restProps
  } = props;

  // Should not happen for listings that go through EditListingWizard.
  // However, this might happen for imported listings.
  if (displayDeliveryMethod && !pickupEnabled && !shippingEnabled) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProductOrderForm.noDeliveryMethodSet" />
      </p>
    );
  }

  const hasOneItemLeft = currentStock === 1;
  const hasOneItemMode = !allowOrdersOfMultipleItems && currentStock > 0;
  const quantityMaybe = hasOneItemLeft || hasOneItemMode ? { quantity: '1' } : {};
  const deliveryMethodMaybe =
    shippingEnabled && !pickupEnabled
      ? { deliveryMethod: 'shipping' }
      : !shippingEnabled && pickupEnabled
      ? { deliveryMethod: 'pickup' }
      : !shippingEnabled && !pickupEnabled
      ? { deliveryMethod: 'none' }
      : {};
  const hasMultipleDeliveryMethods = pickupEnabled && shippingEnabled;
  const initialValues = { ...quantityMaybe, ...deliveryMethodMaybe };
  const handleFormSubmit = values => {
    return onSubmit(values);
  };

  return (
    <FinalForm
      {...restProps}
      initialValues={initialValues}
      hasMultipleDeliveryMethods={hasMultipleDeliveryMethods}
      displayDeliveryMethod={displayDeliveryMethod}
      intl={intl}
      onSubmit={handleFormSubmit}
      render={formRenderProps =>
        renderForm(
          {
            ...formRenderProps,
            buyerProtectionLink,
            price,
            currentStock,
            allowOrdersOfMultipleItems,
            pickupEnabled,
            shippingEnabled,
            shippingCountries,
          },
          {
            onlyMakeOffer,
            showMakeOfferButton,
            onOpenMakeOffer,
            makeOfferInProgress,
            makeOfferError,
            handlePurchaseSubmit: event => {
              return formRenderProps.handleSubmit(event);
            },
          }
        )
      }
    />
  );
};

export default ProductOrderForm;
